// Pure helpers for managing the Purpose Pack version history.
// Extracted from VisionBoard so they're unit-testable.

export type PurposePackData = {
  purpose: string;
  mission: string;
  vision: string;
  values: string[];
  motto: string;
  brand: string;
  leadership: string;
  futureIdentity: string;
};

export type PurposePackWithVersions = PurposePackData & {
  _versions?: Array<{ savedAt: number; pack: PurposePackData }>;
};

export const MAX_PURPOSE_VERSIONS = 20;

const stripVersions = (p: PurposePackWithVersions): PurposePackData => {
  const { _versions: _ignore, ...rest } = p;
  void _ignore;
  return rest;
};

const sameContent = (a: PurposePackData, b: PurposePackData) =>
  a.purpose === b.purpose &&
  a.mission === b.mission &&
  a.vision === b.vision &&
  a.motto === b.motto &&
  a.brand === b.brand &&
  a.leadership === b.leadership &&
  a.futureIdentity === b.futureIdentity &&
  a.values.join("|") === b.values.join("|");

/**
 * Archive `current` into the version history and return a new pack carrying
 * `next` content. Skips archival when content didn't actually change. Caps
 * history at MAX_PURPOSE_VERSIONS (most recent first).
 */
export function archivePurposeVersion(
  current: PurposePackWithVersions | null,
  next: PurposePackData,
  now: number = Date.now(),
): PurposePackWithVersions {
  const existing = current?._versions ?? [];
  if (!current) return { ...next, _versions: [] };

  const currentBare = stripVersions(current);
  if (sameContent(currentBare, next)) {
    return { ...next, _versions: existing };
  }

  const versions = [{ savedAt: now, pack: currentBare }, ...existing].slice(
    0,
    MAX_PURPOSE_VERSIONS,
  );
  return { ...next, _versions: versions };
}

/**
 * Restore the version at `index`. The previously-current pack is itself
 * archived so the user can undo the restore.
 */
export function restorePurposeVersion(
  current: PurposePackWithVersions,
  index: number,
  now: number = Date.now(),
): PurposePackWithVersions {
  const versions = current._versions ?? [];
  const target = versions[index];
  if (!target) return current;
  return archivePurposeVersion(current, target.pack, now);
}
